import React, { useEffect, useRef, useState } from 'react';
import { Gift, BadgeCheck, Building2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { selectTeam } from '../../team/teamSlice';
import { fetchFolders } from '../../library/librarySlice';
import { setUser } from '../../settings/settingsSlice';
import { RootState } from '../../../reducers';
import { stringAvatar } from '../../../lib/textUtils';
import { fetchSubscription } from '../../plan/thunk';
import { saveLastSelectedTeamSpace } from '../../../services/teamService';
import { getTeamUrl } from '../../../lib/routingUtils';
import InviteLinkDialog from '../../team/invite/inviteByLinkModal';
import { TeamRole } from '../../../types/state';

interface TeamSubscription {
  provider: string;
  providerId: string;
  package: string;
  pluginPackage: string;
  unsubscribeModal: string | null;
  upgradeStatus: string;
  packageUpdatedAt: string;
  term: string;
  price: number;
  currentPeriodStartedAt?: string;
  state: 'active' | 'inactive';
}

interface TeamMember {
  _id: string;
  email: string;
  name: string;
  picture: string | null;
  status: boolean;
  role: string;
  createdAt: string;
  inviteAccepted: boolean;
  lastActiveAt: string;
  updatedAt: string;
}

interface TeamBase {
  _id: string;
  teamId: string;
  name: string;
  ownerId: string;
  email: string;
  teamSpaceMembers: TeamMember[];
  spaceMembers: any[];
  createdAt: string;
  updatedAt: string;
  __v?: number;
  joinCode?: string;
  subscription?: TeamSubscription;
}

interface TeamSelectMenuProps {
  onClose?: () => void;
}

const TeamSelectMenu: React.FC<TeamSelectMenuProps> = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { team, library, plan, settings } = useSelector(
    (state: RootState) => state,
  );
  const { teams, selectedTeam } = team;
  const { subscription: currentSubscription } = plan;
  const currentUserId = settings.user?._id;
  const folderLoadingStatus = library?.folders?.status;
  const [loadingTeamId, setLoadingTeamId] = useState<string | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const isInitialRender = useRef(true);

  const handleSelect = async (value: string) => {
    const newSelectedTeam = teams.find((newTeam) => newTeam.teamId === value);
    if (!newSelectedTeam) return;

    setLoadingTeamId(value);
    dispatch(selectTeam(newSelectedTeam));
    dispatch(fetchFolders({ teamId: value }));

    // Update user role for the new team to prevent stale permission state
    if (currentUserId) {
      const currentUserInTeam = (
        newSelectedTeam as unknown as TeamBase
      ).teamSpaceMembers?.find((member) => member._id === currentUserId);

      if (currentUserInTeam?.role) {
        dispatch(setUser({ role: currentUserInTeam.role as TeamRole }));
      } else if (newSelectedTeam.ownerId === currentUserId) {
        // If user is the owner but not in teamSpaceMembers, set role to 'Owner'
        dispatch(setUser({ role: 'Owner' }));
      }
    }

    // XXX Some features need this.
    saveLastSelectedTeamSpace(value);

    // Navigate to the team's library with proper URL structure
    const teamUrl = getTeamUrl({ teamId: value, withBasePath: false });
    navigate(teamUrl);
  };

  useEffect(() => {
    if (isInitialRender.current) {
      // Skip the first render. Subscription is set from initial data fetch in app slice
      isInitialRender.current = false;
    } else if (selectedTeam) {
      const { teamId } = selectedTeam;
      dispatch(
        fetchSubscription({
          teamId,
        }),
      );
    }
  }, [selectedTeam]);

  // Clear loading state when folders finish loading
  useEffect(() => {
    if (
      folderLoadingStatus === 'succeeded' ||
      folderLoadingStatus === 'failed'
    ) {
      setLoadingTeamId(null);
    }
  }, [folderLoadingStatus]);

  const getPlanBadge = (
    planName?: string,
    isSelected?: boolean,
    isInTrigger?: boolean,
  ) => {
    if (!planName) return null;

    const normalized = planName.toLowerCase();

    // Format the plan name and icon
    let displayName = normalized;
    let Icon = BadgeCheck;

    // Special formatting for known plans
    switch (normalized) {
      case 'free':
      case 'free_loggedin':
        displayName = 'Free';
        Icon = Gift;
        break;
      case 'basic':
        displayName = 'Basic';
        break;
      case 'standard':
        displayName = 'Standard';
        break;
      case 'premium':
        displayName = 'Premium';
        break;
      case 'pro':
        displayName = 'Pro';
        break;
      case 'business':
        displayName = 'Business';
        break;
      case 'growth':
        displayName = 'Growth';
        break;
      case 'enterprise':
        displayName = 'Enterprise';
        break;
      default:
        displayName = normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }

    if (isInTrigger) {
      // Minimal style for trigger - no background, muted text
      return (
        <span className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
          <Icon className="h-3 w-3" />
          {displayName}
        </span>
      );
    }

    return (
      <Badge
        variant={isSelected ? 'default' : 'secondary'}
        className={`flex items-center gap-1 h-5 rounded-full px-2 py-0 text-[10px] font-medium ${
          isSelected
            ? ''
            : 'bg-muted/50 text-muted-foreground border-transparent'
        }`}
      >
        <Icon className="h-3 w-3" />
        {displayName}
      </Badge>
    );
  };

  const isLoading = loadingTeamId !== null && folderLoadingStatus === 'pending';

  const handleManageTeams = () => {
    if (typeof window !== 'undefined') {
      (window as any).__bottomActionsModals = {
        ...(window as any).__bottomActionsModals,
        settingsModalOpen: true,
        settingsInitialTab: 'team',
      };
    }
    onClose?.();
  };

  // If user only has one team space, show "Create Team" button instead of dropdown
  if (teams.length === 1) {
    return (
      <>
        <div className="px-3">
          <button
            type="button"
            onClick={() => setShowInviteDialog(true)}
            className="flex w-full items-center rounded-md px-3 py-2 text-sm font-bold transition-colors hover:bg-accent border border-input"
          >
            <Building2 className="mr-3 h-4 w-4" strokeWidth={2} />
            <span>Create Team</span>
          </button>
        </div>
        {showInviteDialog && (
          <InviteLinkDialog onClose={() => setShowInviteDialog(false)} />
        )}
      </>
    );
  }

  return (
    <div className="px-3 py-2">
      <Select
        value={selectedTeam?.teamId || ''}
        onValueChange={handleSelect}
        disabled={isLoading}
      >
        <SelectTrigger className="w-full [&>span]:w-full">
          <SelectValue placeholder="Select a team">
            {selectedTeam && (
              <div className="relative flex items-center w-full pr-16">
                <div className="flex items-center min-w-0">
                  <div className="mr-2 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded bg-primary text-[10px] font-medium text-primary-foreground">
                    {stringAvatar(selectedTeam.name).children}
                  </div>
                  <span className="truncate">{selectedTeam.name}</span>
                </div>
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {getPlanBadge(
                    currentSubscription?.package || 'free',
                    true,
                    true,
                  )}
                </div>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {(teams as TeamBase[]).map(({ name, teamId, subscription }) => {
            const { children: avatarText } = stringAvatar(name);
            const isSelected = selectedTeam?.teamId === teamId;
            // Use current subscription from plan state for selected team, fallback to team's subscription
            const teamSubscription = isSelected
              ? currentSubscription
              : subscription;
            return (
              <SelectItem
                key={teamId}
                value={teamId}
                className={`relative pr-24 ${isSelected ? 'bg-accent/50' : ''}`}
              >
                <div className="flex items-center">
                  <div
                    className={`mr-2 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded text-[10px] font-medium ${
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {avatarText}
                  </div>
                  <span className="truncate">{name}</span>
                </div>
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {getPlanBadge(
                    teamSubscription?.package || 'free',
                    isSelected,
                  )}
                </div>
              </SelectItem>
            );
          })}
          <SelectSeparator />
          <button
            type="button"
            onClick={handleManageTeams}
            className="w-full rounded-sm px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground text-left"
          >
            Manage Teams
          </button>
        </SelectContent>
      </Select>
    </div>
  );
};

export default TeamSelectMenu;
